import { useEffect, useState } from 'react';
import { adminApi, errorText, type MailTemplateView } from './api';
import { CardShell } from './card';

/**
 * Reiter „E-Mails": die sieben Mails des Projekts mit Betreff und Text. Platzhalter in geschweiften
 * Klammern, Vorschau mit Beispieldaten, Zurücksetzen auf den Standardtext der Plattform.
 */
export function MailsPage() {
  return (
    <>
      <div className="admin-head">
        <div>
          <h1>E-Mails</h1>
          <p className="muted">Was die Plattform in deinem Namen verschickt – für alle Webinare des Projekts. Absender: die Adresse aus den Projekteinstellungen.</p>
        </div>
      </div>
      <MailTemplates />
    </>
  );
}

/** Die Liste der sieben Mails – fürs Projekt oder, mit webinarId, als eigene Fassung eines Webinars. */
export function MailTemplates({ webinarId }: { webinarId?: string }) {
  const [mails, setMails] = useState<MailTemplateView[] | null>(null);
  const [placeholders, setPlaceholders] = useState<{ key: string; description: string }[]>([]);

  useEffect(() => {
    adminApi
      .listMails(webinarId)
      .then((r) => {
        setMails(r.mails);
        setPlaceholders(r.placeholders);
      })
      .catch(() => setMails([]));
  }, [webinarId]);

  const replace = (m: MailTemplateView) => setMails((list) => (list ?? []).map((x) => (x.kind === m.kind ? m : x)));

  return (
    <>
      <details className="card" style={{ marginBottom: 24 }}>
        <summary>
          <strong>Platzhalter</strong> <span className="muted small">– werden beim Versand ausgefüllt; ein Absatz mit leerem Platzhalter fällt weg</span>
        </summary>
        <table className="table" style={{ marginTop: 12 }}>
          <tbody>
            {placeholders.map((p) => (
              <tr key={p.key}>
                <td>
                  <code>{`{${p.key}}`}</code>
                </td>
                <td className="muted">{p.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted small" style={{ marginTop: 12 }}>
          Absätze trennst du mit einer Leerzeile. Der erste Absatz, der nur aus einem Link-Platzhalter besteht, wird in der Mail zum Knopf. „» " am Anfang eines Absatzes macht ein Zitat.
        </p>
      </details>

      {mails === null && <p className="muted">Wird geladen …</p>}
      {mails?.map((m) => (
        <MailCard key={m.kind} mail={m} webinarId={webinarId} onChanged={replace} />
      ))}
    </>
  );
}

const SOURCE_LABEL: Record<MailTemplateView['source'], string> = { default: 'Standardtext', project: 'wie Projekt', webinar: 'eigene Fassung' };

function MailCard({ mail, webinarId, onChanged }: { mail: MailTemplateView; webinarId?: string | undefined; onChanged: (m: MailTemplateView) => void }) {
  const [subject, setSubject] = useState(mail.subject);
  const [body, setBody] = useState(mail.body);
  const [preview, setPreview] = useState<{ subject: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = subject !== mail.subject || body !== mail.body;

  useEffect(() => {
    setSubject(mail.subject);
    setBody(mail.body);
    setPreview(null);
  }, [mail]);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await action();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    run(async () => {
      onChanged(await adminApi.saveMail(mail.kind, { subject, body }, webinarId));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    });
  const reset = () =>
    run(async () => {
      if (!window.confirm(webinarId ? 'Eigene Fassung verwerfen und wieder die Vorlage des Projekts nehmen?' : 'Diese Mail auf den Standardtext der Plattform zurücksetzen? Deine Änderungen gehen verloren.')) return;
      onChanged(await adminApi.resetMail(mail.kind, webinarId));
    });
  const showPreview = () => run(async () => setPreview(await adminApi.previewMail(mail.kind, { subject, body })));

  return (
    <CardShell
      as="form"
      id={`mail.${webinarId ? 'webinar.' : ''}${mail.kind}`}
      defaultOpen={false}
      style={{ marginBottom: 20 }}
      title={mail.label}
      right={<span className={`tag ${mail.custom ? 'tag--active' : 'tag--draft'}`}>{webinarId ? SOURCE_LABEL[mail.source] : mail.custom ? 'angepasst' : 'Standardtext'}</span>}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <p className="muted small">Wann: {mail.when}.</p>
      <div className="field">
        <label htmlFor={`m-subject-${mail.kind}`}>Betreff</label>
        <input id={`m-subject-${mail.kind}`} type="text" value={subject} maxLength={300} onChange={(e) => setSubject(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor={`m-body-${mail.kind}`}>Text</label>
        <textarea id={`m-body-${mail.kind}`} value={body} rows={Math.min(24, Math.max(8, body.split('\n').length + 1))} maxLength={10000} onChange={(e) => setBody(e.target.value)} required />
        {mail.required.length > 0 && (
          <span className="faint small">
            Muss enthalten: {mail.required.map((r) => `{${r}}`).join(', ')}
          </span>
        )}
      </div>
      {preview && (
        <div className="mail-preview">
          <div className="label">Vorschau mit Beispieldaten</div>
          <p>
            <strong>{preview.subject}</strong>
          </p>
          <pre>{preview.text}</pre>
        </div>
      )}
      <div className="save-bar">
        <button type="submit" className="button" disabled={busy || !dirty}>
          {busy ? 'Bitte warten …' : 'Speichern'}
        </button>
        <button type="button" className="button button--secondary" disabled={busy} onClick={() => void showPreview()}>
          Vorschau
        </button>
        {mail.custom && (
          <button type="button" className="link-button" disabled={busy} onClick={() => void reset()}>
            {webinarId ? 'Wieder wie Projekt' : 'Auf Standard zurücksetzen'}
          </button>
        )}
        {saved && <span className="muted small">Gespeichert.</span>}
        {error && <span className="error-inline">{error}</span>}
      </div>
    </CardShell>
  );
}
